/**
 * Command Center — the admin index page (mirrors apps/experts/public/admin/
 * index.html, whose <title> is "Command Center"). A dashboard of links into the
 * other admin surfaces plus an at-a-glance summary stitched from the data the
 * other pages own: ops-dashboard (eval/cron/deploys/llm) + status (service
 * health). It reuses the SAME query hooks the dedicated pages use, so there's
 * one fetch path, not a parallel one.
 */
import { Link } from 'react-router';
import { ADMIN_NAV } from '../../admin-nav';
import {
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Panel,
  StatusDot,
  toneForStatus,
} from '../../components/primitives';
import { useOpsDashboard, useStatusPage } from '../../data-access/useAdmin';
import { isPendingSection, relTime } from '../../lib/format';

function HealthTile() {
  const { data, isPending, isError } = useStatusPage();
  if (isPending) return <AdminLoading />;
  if (isError || !data)
    return <AdminError message="Failed to load service health." />;
  return (
    <ul className="flex flex-col gap-1.5">
      {data.services.map((svc) => (
        <li className="flex items-center gap-2 font-inter text-sm" key={svc.id}>
          <StatusDot label={svc.status} tone={toneForStatus(svc.status)} />
          <span className="text-content-primary">{svc.label}</span>
          <span className="ml-auto text-content-tertiary text-xs">
            {svc.latency_ms != null ? `${svc.latency_ms}ms` : svc.status}
          </span>
        </li>
      ))}
    </ul>
  );
}

function LoopTile() {
  const { data, isPending, isError } = useOpsDashboard();
  if (isPending) return <AdminLoading />;
  if (isError || !data)
    return <AdminError message="Failed to load ops summary." />;

  const evalRuns = data.eval_runs;
  const cron = data.cron;
  const latestEval = isPendingSection(evalRuns)
    ? null
    : (evalRuns.runs[0] ?? null);
  const latestCron = isPendingSection(cron)
    ? null
    : (cron.executions[0] ?? null);

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 font-inter text-sm">
      <dt className="text-content-secondary">Latest eval</dt>
      <dd className="text-content-primary">
        {latestEval?.score != null ? latestEval.score.toFixed(2) : '—'}
      </dd>
      <dt className="text-content-secondary">Eval ran</dt>
      <dd className="text-content-primary">{relTime(latestEval?.ran_at)}</dd>
      <dt className="text-content-secondary">Last cron</dt>
      <dd className="flex items-center gap-1.5 text-content-primary">
        {latestCron ? (
          <>
            <StatusDot tone={toneForStatus(latestCron.status)} />
            {latestCron.job_name
              .replace('caj-andavo-labs-', '')
              .replace('-dev', '')}
          </>
        ) : (
          '—'
        )}
      </dd>
      <dt className="text-content-secondary">Cron at</dt>
      <dd className="text-content-primary">
        {relTime(latestCron?.started_at)}
      </dd>
    </dl>
  );
}

export function CommandCenterPage() {
  // Every admin destination except this page. ADMIN_NAV is derived from the
  // grouped nav model, so a page can't appear in one and not the other.
  const surfaces = ADMIN_NAV.filter(
    (item) => item.path !== '/admin/command-center',
  );

  return (
    <div>
      <AdminPageHeader
        subtitle="Operational overview and entry point to every admin surface."
        title="Command Center"
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Service health">
          <HealthTile />
        </Panel>
        <Panel title="Loop & cron">
          <LoopTile />
        </Panel>
      </div>

      <Panel className="mt-4" title="Admin surfaces">
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {surfaces.map((item) => (
            <li key={item.path}>
              <Link
                className="block rounded-md border border-border-subtle px-3 py-2 font-inter text-content-primary text-sm transition-colors hover:bg-surface-background"
                to={item.path}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

export default CommandCenterPage;
